import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Api } from '@api/api';
import { getAdditionalServices } from '@api/functions';
import {
  ExceptionCode,
  type AdditionalServiceDto,
  type PopulatedCartItemDto,
  type ProductListItemDto,
} from '@api/models';
import { MINIMAL_ORDER_COST } from '../../constants';
import { RouterLinkWithLang } from '../../directives/router-link-with-lang/router-link-with-lang.directive';
import { CartService } from '../../services/cart.service';
import { ScrollToService } from '../../services/scroll-to/scroll-to.service';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { IconComponent } from "../icon/icon.component";
import { PreloaderComponent } from "../preloader/preloader.component";
import { QuantityControlComponent } from '../quantity-control/quantity-control.component';
import { CrossSellProductsComponent } from './components/cross-sell-products/cross-sell-products.component';
import { ProductDiscountsComponent } from '../product-discounts/product-discounts.component';
import { resolveActivePriceIndex } from '../../helpers/resolve-active-price-index.function';
import { resolveHttpErrorMessage } from '../../helpers/resolve-http-error-message.helper';

@Component({
  selector: 'cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    QuantityControlComponent,
    CrossSellProductsComponent,
    ProductDiscountsComponent,
    TranslatePipe,
    RouterLink,
    RouterLinkWithLang,
    PreloaderComponent,
    IconComponent,
    MatMenu,
    MatMenuTrigger,
  ],
})
export class CartComponent implements OnInit {
  readonly cartService = inject(CartService);
  private readonly api = inject(Api);
  private readonly scrollToService = inject(ScrollToService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  readonly sourceForAnalytics = input.required<string>();
  readonly continueShopping = output();
  readonly goToCheckout = output();
  private readonly qtyCmps = viewChildren(QuantityControlComponent);

  readonly error = signal<string | null>(null);
  readonly additionalServices = signal<AdditionalServiceDto[]>([]);
  readonly canPlaceOrder = computed(() => this.cartService.cartPrices()?.totalCost >= this.minimalOrderCost);
  readonly outOfStockIndex = computed(() => {
    return this.cartService.inAndOutOfStockCartItemsSorted().findIndex(item => !item.isInStock);
  });

  readonly minimalOrderCost = MINIMAL_ORDER_COST;
  private errorTimeoutRef: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    effect(() => {
      const cartItems = this.cartService.cartItems();
      untracked(() => {
        this.setAdditionalServices(cartItems);
      });
    });
  }

  ngOnInit(): void {
    this.googleAnalyticsService.showCart(this.sourceForAnalytics());
  }

  getCartItem(productId: number): PopulatedCartItemDto {
    return this.cartService.cartItems().find(item => item.productId === productId);
  }

  getProduct(productId: number): ProductListItemDto {
    return this.cartService.products().find(product => product.id === productId);
  }

  onQtyChange(cartItemIndex: number, qty: number) {
    this.resetCartError();

    const cartItem = this.cartService.inAndOutOfStockCartItemsSorted()[cartItemIndex];
    const product = this.getProduct(cartItem.productId);

    this.cartService.setCartItem({
      productId: cartItem.productId,
      qty: qty,
      additionalServiceIds: cartItem.additionalServiceIds,
      analyticsData: { sku: product.sku, name: product.name, price: product.price, source: 'cart' },
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const updatedItem = this.getCartItem(product.id);
          if (updatedItem.errorCode === ExceptionCode.NOT_ENOUGH_QUANTITY_IN_STOCK) {
            this.qtyCmps().at(cartItemIndex)?.setValue(cartItem.qty, false);
          }
        },
        error: error => {
          this.qtyCmps().at(cartItemIndex)?.setValue(cartItem.qty, false);
          this.setCartError(error);
        },
      });
  }

  deleteFromCart(product: ProductListItemDto): void {
    this.resetCartError();

    this.cartService.removeCartItem(
      product.id,
      { sku: product.sku, name: product.name, price: product.price, source: 'cart' },
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => this.setCartError(error),
      });
  }

  moveCartItemToWishlist(product: ProductListItemDto): void {
    this.resetCartError();

    this.cartService.moveCartItemToWishlist(
      product.id,
      { sku: product.sku, name: product.name, price: product.price, source: 'cart' },
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: error => this.setCartError(error),
      });
  }

  checkout() {
    this.resetCartError();
    this.goToCheckout.emit();
  }

  private setCartError(error) {
    clearTimeout(this.errorTimeoutRef);

    this.error.set(resolveHttpErrorMessage(error));
    this.errorTimeoutRef = setTimeout(() => this.resetCartError(), 10000);
    this.scrollToService.scrollTo({ target: this.elementRef, offset: -60 });
  }

  private resetCartError() {
    this.error.set(null);
    clearTimeout(this.errorTimeoutRef);
  }

  getItemImg(product: ProductListItemDto): string {
    if (!product.mediaUrl) {
      return '/assets/images/no-img.jpg';
    } else {
      return product.mediaUrl;
    }
  }

  getActivePriceIndex(product: ProductListItemDto): number | undefined {
    return resolveActivePriceIndex(product, this.cartService.cartPrices()?.itemsCost);
  }

  getItemCostWithDiscount(product: ProductListItemDto): number {
    const activePriceIndex = this.getActivePriceIndex(product);
    if (activePriceIndex === undefined) {
      return null;
    }

    const cartItem = this.getCartItem(product.id);
    return cartItem.qty * product.discountedPrices[activePriceIndex].price;
  }

  getAdditionalService(serviceId: number): AdditionalServiceDto | undefined {
    return this.additionalServices().find(service => service.id === serviceId);
  }

  private setAdditionalServices(cartItems: PopulatedCartItemDto[]): void {
    const additionalServiceIds = cartItems.flatMap(item => item.additionalServiceIds);
    if (!additionalServiceIds.length) {
      this.additionalServices.set([]);
      return;
    }

    this.api.invoke(getAdditionalServices, { ids: additionalServiceIds.map(id => id.toString()) })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => this.additionalServices.set(response.data),
      });
  }

  onCrossSellShowCart(_productId: number): void {
    this.scrollToService.scrollTo({ target: this.elementRef, offset: -50 });
  }
}
